import React from "react";
import { useTranslation } from "react-i18next";
import {
  MediaDetailObservation,
  ObservationSource,
} from "../common/ObjectTypes";
import { observationTypeLabel } from "./SuggestionCard";
import ReviewCardBody from "./ReviewCardBody";
import { DetailBox, boxIdsForOwner } from "./mediaDetailComposer";
import { tailwindClassnames } from "../common/TailwindClassnames";
import { EditPencilIcon, DeleteXIcon } from "../common/ProjectIcons";

/**
 * Human-readable label for an observation source. Values come from the API
 * enum; the display strings are translated.
 */
export const observationSourceLabel = (
  t: (key: string) => string,
  source: ObservationSource,
): string => {
  switch (source) {
    case "human":
      return t("Human");
    case "ai_approved":
      return t("AI approved");
    case "ai_edited":
      return t("AI edited");
    default:
      return t("Imported");
  }
};

type ObservationListProps = {
  observations: MediaDetailObservation[];
  /**
   * AKL-712: the caller's composed overlay boxes (`composeDetailBoxes`), so
   * each row's highlight set is read via `boxIdsForOwner` — the box's actual,
   * de-duplicated owner — rather than the observation's own (possibly stale)
   * `geometries` list. A box that has since split into a different observation
   * no longer shows under this row once that owner changes.
   */
  boxes: DetailBox[];
  /** Per-row commit handlers (AKL-682). Omit for a read-only list. */
  onEdit?: (observation: MediaDetailObservation) => void;
  onDelete?: (observation: MediaDetailObservation) => void;
  /** Disables the row controls while an action is committing. */
  busy?: boolean;
  /**
   * AKL-698: pointer-selected observation id (as string, matches the surface's
   * `selectedCardKey`). The matching row gets the pointer-selected visual.
   */
  selectedRowKey?: string | null;
  /**
   * AKL-698: fired on pointer enter / leave of a row that has at least one
   * linked bounding box — i.e. the API returned geometry for it (AKL-703).
   * That includes media-scope rows: an aggregate accept is persisted as a
   * media-scope observation yet carries every box it was committed from, so the
   * gate is "has geometry", not "scope is object". Receives the row's identity
   * key on enter, `null` on leave.
   */
  onRowHover?: (rowKey: string | null, boxIds: string[]) => void;
  /**
   * AKL-698: fired on pointer / keyboard activation of a row body (not the
   * inner buttons). Same gate as `onRowHover`: only rows with at least one
   * linked bounding box are clickable. Rows with no geometry do not receive
   * `role="button"` / `tabIndex` and do not fire this callback.
   */
  onRowClick?: (rowKey: string, boxIds: string[]) => void;
};

/** Stable pointer-selection key for a row — the observation's id as a string. */
const rowKeyFor = (obs: MediaDetailObservation): string => String(obs.id);

/**
 * List of committed observations on the photo (AKL-698 restyle). Each non-
 * deleted row renders through the shared `ReviewCardBody` — the same body the
 * suggestion cards use, so the Species / Count block reads identically — as the
 * "accepted" variant: a green `✓ accepted` chip plus observation-type + source
 * chips in the header, the species name (with its Latin name in italics) on its
 * own line, and the count line carrying pencil / red-X icon buttons for Edit /
 * Delete (right-aligned) so the species line stays free for the full name.
 * Rows the API returned geometry for participate in the card ↔ bbox highlight
 * linkage; rows with no box (manual observations, blank markers) do not.
 */
const ObservationList: React.FC<ObservationListProps> = ({
  observations,
  boxes,
  onEdit,
  onDelete,
  busy,
  selectedRowKey,
  onRowHover,
  onRowClick,
}) => {
  const { t } = useTranslation();
  const visible = (observations ?? []).filter((o) => o.status !== "deleted");

  if (visible.length === 0) {
    return (
      <p
        className="text-sm text-off-black-light/70 dark:text-primary-off-white/50"
        data-testid="observation-list-empty"
      >
        {t("No observations recorded for this photo.")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2" data-testid="observation-list">
      {visible.map((obs) => {
        const rowKey = rowKeyFor(obs);
        const boxIds = boxIdsForOwner(boxes, rowKey);
        // Linkage is active whenever the API returned geometry for this
        // observation — regardless of persisted scope. An aggregate accept comes
        // back with `applies_to: "media"` and no `source_prediction_id`, but the
        // API still resolves every box it was committed from (AKL-703).
        const hasLinkedBoxes = boxIds.length > 0;
        const isSelected = selectedRowKey != null && selectedRowKey === rowKey;
        const rowClasses = [
          "rounded-lg border border-black/10 bg-black/5 px-3 py-2 dark:border-white/10 dark:bg-white/5",
          tailwindClassnames.resolvedBarAccepted,
          isSelected ? tailwindClassnames.cardPointerSelected : "",
          hasLinkedBoxes && onRowClick ? "cursor-pointer" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
          if (!hasLinkedBoxes || !onRowClick) return;
          const target = e.target as HTMLElement | null;
          if (target?.closest?.("button, a, input, textarea, select")) return;
          onRowClick(rowKey, boxIds);
        };
        const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
          if (!hasLinkedBoxes || !onRowClick) return;
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRowClick(rowKey, boxIds);
          }
        };
        return (
          <li
            key={obs.id}
            className={rowClasses}
            role={hasLinkedBoxes && onRowClick ? "button" : undefined}
            tabIndex={hasLinkedBoxes && onRowClick ? 0 : undefined}
            aria-pressed={hasLinkedBoxes && onRowClick ? isSelected : undefined}
            onMouseEnter={
              hasLinkedBoxes && onRowHover
                ? () => onRowHover(rowKey, boxIds)
                : undefined
            }
            onMouseLeave={
              hasLinkedBoxes && onRowHover
                ? () => onRowHover(null, [])
                : undefined
            }
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            data-testid="observation-row"
            data-selected={isSelected ? "true" : undefined}
          >
            <ReviewCardBody
              header={
                <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                  <span
                    className={tailwindClassnames.resolvedChipAccepted}
                    data-testid="observation-row-state"
                  >
                    {"✓ " + t("accepted")}
                  </span>
                  <span className="rounded bg-black/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-off-black-light dark:bg-white/10 dark:text-primary-off-white/80">
                    {observationTypeLabel(t, obs.observation_type)}
                  </span>
                  <span className="rounded bg-sky-blue-light/40 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-storm-blue dark:bg-sky-blue-light/20 dark:text-sky-blue-light">
                    {observationSourceLabel(t, obs.source)}
                  </span>
                </div>
              }
              speciesName={obs.display_name || t("Unknown")}
              scientificName={obs.scientific_name}
              count={obs.individual_count}
              countTitle={
                obs.individual_count != null
                  ? String(obs.individual_count)
                  : undefined
              }
              countTrailing={
                <span className="flex shrink-0 items-center gap-1">
                  {onEdit && (
                    <button
                      type="button"
                      className="rounded p-1 hover:bg-black/10 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none disabled:opacity-40 dark:hover:bg-white/10"
                      onClick={() => onEdit(obs)}
                      disabled={busy}
                      aria-label={t("Edit observation")}
                      data-testid="observation-edit"
                    >
                      <EditPencilIcon />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      className="rounded p-1 hover:bg-red-600/10 focus-visible:ring-2 focus-visible:ring-red-400 focus:outline-none disabled:opacity-40 dark:hover:bg-red-500/20"
                      onClick={() => onDelete(obs)}
                      disabled={busy}
                      aria-label={t("Delete observation")}
                      data-testid="observation-delete"
                    >
                      <DeleteXIcon />
                    </button>
                  )}
                </span>
              }
            />

            {obs.comments && (
              <p className="mt-1.5 text-xs text-off-black-light/70 dark:text-primary-off-white/60">
                {obs.comments}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default ObservationList;
